/**
 * Sentry error tracking initialization for the school portal.
 */

import * as Sentry from '@sentry/react'

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN || ''
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT || 'development'
const IS_PRODUCTION = ENVIRONMENT === 'production' || ENVIRONMENT === 'staging'

export function initSentry(): void {
  if (!SENTRY_DSN || !IS_PRODUCTION) {
    console.log('[Sentry] Skipping initialization (no DSN or development mode)')
    return
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    environment: ENVIRONMENT,

    tracesSampleRate: ENVIRONMENT === 'production' ? 0.1 : 1.0,

    replaysSessionSampleRate: ENVIRONMENT === 'staging' ? 0.1 : 0,
    replaysOnErrorSampleRate: ENVIRONMENT === 'staging' ? 1.0 : 0,

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    beforeSend(event, _hint) {
      if (event.message?.includes('ResizeObserver')) return null
      if (event.message?.includes('Network request failed')) return null
      if (event.message?.includes('canceled')) return null
      return event
    },

    beforeSendTransaction(event) {
      if (event.contexts?.trace?.data) {
        delete event.contexts.trace.data
      }
      return event
    },
  })

  console.log(`[Sentry] Initialized for ${ENVIRONMENT}`)
}

export function setSentryUser(user: {
  id: string
  email?: string
  role_labels?: string[]
  tenantId?: string
}): void {
  if (!IS_PRODUCTION) return

  Sentry.setUser({
    id: user.id,
    ...(ENVIRONMENT !== 'production' && { email: user.email }),
  })

  if (user.tenantId) {
    Sentry.setTag('tenant_id', user.tenantId)
  }

  if (user.role_labels?.length) {
    Sentry.setTag('user_roles', user.role_labels.join(', '))
  }
}

export function clearSentryUser(): void {
  Sentry.setUser(null)
  Sentry.setTag('tenant_id', undefined)
  Sentry.setTag('user_role', undefined)
}

export function captureException(
  error: Error,
  context?: Record<string, unknown>
): void {
  if (!IS_PRODUCTION) {
    console.error('[Sentry] Would capture:', error, context)
    return
  }

  Sentry.withScope((scope) => {
    if (context) {
      scope.setExtras(context)
    }
    Sentry.captureException(error)
  })
}

export const ErrorBoundary = Sentry.ErrorBoundary
